import queryString from "query-string";

export const DEFAULT_ROOM_GUESTS = [
  { noOfAdults: 2, noOfChilds: 0, childAge: [] },
];

const normalizeRoomGuestEntry = (room = {}) => {
  const noOfAdults = Number(room?.noOfAdults ?? room?.NoOfAdults ?? 1) || 1;
  const noOfChilds = Number(room?.noOfChilds ?? room?.NoOfChilds ?? 0) || 0;
  if (noOfChilds <= 0) {
    return { noOfAdults, noOfChilds: 0, childAge: [] };
  }
  let childAge = Array.isArray(room?.childAge ?? room?.ChildAge)
    ? (room.childAge ?? room.ChildAge)
        .map((age) => String(age ?? "").trim())
        .filter((age) => age !== "")
    : [];
  childAge = childAge.slice(0, noOfChilds);
  while (childAge.length < noOfChilds) {
    childAge.push("5");
  }
  return { noOfAdults, noOfChilds, childAge };
};

/** URL `roomGuests` is JSON string; missing/invalid must not crash listing. */
export const parseRoomGuestsParam = (
  raw,
  fallback = DEFAULT_ROOM_GUESTS,
) => {
  if (!raw) {
    return [...fallback];
  }
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [...fallback];
    }
    return parsed.map((room) => normalizeRoomGuestEntry(room));
  } catch {
    return [...fallback];
  }
};

/** Normalize roomGuests array from URL object (hotel details / checkout). */
export const normalizeRoomGuestsList = (
  roomGuests,
  fallback = DEFAULT_ROOM_GUESTS,
) => {
  if (!Array.isArray(roomGuests) || roomGuests.length === 0) {
    return [...fallback];
  }
  return roomGuests.map((room) => normalizeRoomGuestEntry(room));
};

/**
 * Search URL roomGuests win over API/search-log (same rule as stay dates on detail).
 */
export const resolveSearchRoomGuests = (
  urlRaw,
  apiRoomGuests,
  fallback = DEFAULT_ROOM_GUESTS,
) => {
  const fromUrl = parseRoomGuestsParam(urlRaw, []);
  if (fromUrl.length > 0) {
    return fromUrl;
  }
  return normalizeRoomGuestsList(apiRoomGuests, fallback);
};

/**
 * Normalizes hotelCityCode from URL / form (plain id, query string, or labelInValue object).
 */
export const parseHotelCityCode = (raw) => {
  if (raw == null || raw === "") {
    return { cityId: null, cityName: null };
  }

  if (typeof raw === "object") {
    const value = raw.value ?? raw.cityId ?? raw.hotelCityCode;
    const label = raw.label ?? raw.cityName;
    if (value != null && typeof value === "object") {
      return parseHotelCityCode(value);
    }
    if (value != null && String(value).includes("=")) {
      const parsed = parseHotelCityCode(String(value));
      return {
        cityId: parsed.cityId,
        cityName: parsed.cityName || (label != null ? String(label) : null),
      };
    }
    return {
      cityId: value != null ? String(value) : null,
      cityName: label != null ? String(label) : null,
    };
  }

  const str = String(raw).trim();
  if (!str) {
    return { cityId: null, cityName: null };
  }

  const normalized = str.replace(/&&/g, "&");
  const parsed = queryString.parse(normalized);

  if (parsed.cityId != null && String(parsed.cityId).trim() !== "") {
    return {
      cityId: String(parsed.cityId).trim(),
      cityName:
        parsed.cityName != null ? String(parsed.cityName).trim() : null,
    };
  }

  // Plain city id in URL: hotelCityCode=59695
  if (!str.includes("=") && /^[A-Za-z0-9_-]+$/.test(str)) {
    return { cityId: str, cityName: null };
  }

  // query-string quirk: parse("59695") => { "59695": null }
  const keys = Object.keys(parsed);
  if (
    keys.length === 1 &&
    (parsed[keys[0]] == null || parsed[keys[0]] === "")
  ) {
    return { cityId: keys[0], cityName: null };
  }

  return { cityId: null, cityName: null };
};

/** Split autosuggest label and extract destination ISO code when present. */
export const parseDestinationFromCityLabel = (raw) => {
  const { cityId, cityName } = parseHotelCityCode(raw);
  if (!cityName || !String(cityName).includes(",")) {
    return { cityId, cityName, destinationCountryCode: null };
  }
  const parts = String(cityName)
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  const countryToken = parts[parts.length - 1] ?? "";
  const destinationCountryCode = /^[A-Za-z]{2}$/.test(countryToken)
    ? countryToken.toUpperCase()
    : null;
  return {
    cityId,
    cityName,
    destinationCountryCode,
    destinationCountryName:
      destinationCountryCode ? null : countryToken || null,
  };
};

export const hasHotelListingSearchParams = (search = "") => {
  const params = queryString.parse(search || window.location.search);
  const { cityId } = parseHotelCityCode(params.hotelCityCode);
  return Boolean(params.checkInDate && params.checkOutDate && cityId);
};

export const hotelDetCardStorageKeys = (traceId, hotelId, userId) => {
  const userKey = userId ?? "guest";
  return [
    `hotelDetCard:${traceId}:${hotelId}:${userKey}`,
    `hotelDetCard:${traceId}:${hotelId}`,
  ];
};

export const hotelDetSupplierParamKey = (traceId, hotelId) =>
  `hotelDetSp:${traceId}:${hotelId}`;

const HOTEL_DET_CARD_PREVIEW_MAX_CHARS = 180_000;

/** sessionStorage is per-tab; localStorage lets Choose Room open details in a new tab. */
export const writeHotelDetCardPreview = (traceId, hotelId, userId, payload) => {
  if (!traceId || !hotelId || payload == null) return;
  let raw;
  try {
    // Never block Choose Room on multi‑MB supplierParamter — that goes in writeHotelDetSupplierParamter.
    const {
      supplierParamter: _sp,
      SupplierParamter: _sp2,
      ...safePayload
    } = typeof payload === "object" && payload ? payload : { value: payload };
    raw = JSON.stringify(safePayload);
  } catch {
    return;
  }
  if (raw.length > HOTEL_DET_CARD_PREVIEW_MAX_CHARS) {
    return;
  }
  hotelDetCardStorageKeys(traceId, hotelId, userId).forEach((key) => {
    try {
      sessionStorage.setItem(key, raw);
    } catch {
      // quota / private mode
    }
    try {
      localStorage.setItem(key, raw);
    } catch {
      // Quota full — drop other hotelDetCard caches only, then retry once.
      try {
        const doomed = [];
        for (let i = 0; i < localStorage.length; i += 1) {
          const k = localStorage.key(i);
          if (k && k.startsWith("hotelDetCard:") && k !== key) doomed.push(k);
        }
        doomed.forEach((k) => localStorage.removeItem(k));
        localStorage.setItem(key, raw);
      } catch {
        // give up on LS; sessionStorage may still have it
      }
    }
  });
};

/**
 * Heavy supplier blob — write AFTER opening the details tab (setTimeout), localStorage only.
 * HotelDet polls briefly so rooms fetch can include it without freezing Choose Room.
 */
export const writeHotelDetSupplierParamter = (traceId, hotelId, supplierParamter) => {
  if (!traceId || !hotelId || supplierParamter == null) return;
  try {
    const key = hotelDetSupplierParamKey(traceId, hotelId);
    const raw =
      typeof supplierParamter === "string"
        ? supplierParamter
        : JSON.stringify(supplierParamter);
    try {
      localStorage.setItem(key, raw);
    } catch {
      try {
        const doomed = [];
        for (let i = 0; i < localStorage.length; i += 1) {
          const k = localStorage.key(i);
          if (k && k.startsWith("hotelDetSp:") && k !== key) doomed.push(k);
        }
        doomed.forEach((k) => localStorage.removeItem(k));
        localStorage.setItem(key, raw);
      } catch {
        // ignore — rooms still load via hotelCode
      }
    }
  } catch {
    // ignore
  }
};

export const readHotelDetSupplierParamter = (traceId, hotelId) => {
  if (!traceId || !hotelId) return null;
  try {
    const raw = localStorage.getItem(hotelDetSupplierParamKey(traceId, hotelId));
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  } catch {
    return null;
  }
};

/** Wait briefly for deferred Choose Room supplier write (new tab race). */
export const waitForHotelDetSupplierParamter = async (
  traceId,
  hotelId,
  { attempts = 10, intervalMs = 50 } = {},
) => {
  const first = readHotelDetSupplierParamter(traceId, hotelId);
  if (first != null) return first;
  for (let i = 0; i < attempts; i += 1) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const next = readHotelDetSupplierParamter(traceId, hotelId);
    if (next != null) return next;
  }
  return null;
};

export const readHotelDetCardPreview = (traceId, hotelId, userId) => {
  if (!traceId || !hotelId) return null;
  const keys = hotelDetCardStorageKeys(traceId, hotelId, userId);
  for (const key of keys) {
    for (const store of [sessionStorage, localStorage]) {
      try {
        const raw = store.getItem(key);
        if (raw) return JSON.parse(raw);
      } catch {
        // try next
      }
    }
  }
  return null;
};
